import { useState, useEffect, memo } from 'react';
import { Link } from 'react-router-dom';
import { Gavel, Loader, ArrowRight } from 'lucide-react';
import { appealsApiService } from '../../services/appealsApi';
import { AppealsByBan } from '../../types/appeals';
import { AppealStatusBadge } from './AppealStatusBadge';
import { AppealVersionBadge } from './AppealVersionBadge';
import { getV2Outcome, V2_OUTCOME_META } from '../../utils/appealsV2';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '../../utils/cn';

interface BanAppealsSectionProps {
  banId: number | string;
  className?: string;
}

export const BanAppealsSection = memo(({ banId, className }: BanAppealsSectionProps) => {
  const [appeals, setAppeals] = useState<AppealsByBan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!banId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    appealsApiService.getAppealsByBan(banId)
      .then(data => {
        if (!cancelled) setAppeals(data);
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load appeal history');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [banId]);

  if (loading) {
    return (
      <div className={cn('', className)}>
        <div className="flex items-center gap-2 mb-2">
          <Gavel className="w-4 h-4 text-purple-400" />
          <span className="text-sm font-semibold text-white">Appeals</span>
        </div>
        <div className="flex items-center py-2">
          <Loader className="w-4 h-4 animate-spin text-accent-400" />
        </div>
      </div>
    );
  }

  if (error || appeals.length === 0) {
    return (
      <div className={cn('', className)}>
        <div className="flex items-center gap-2 mb-2">
          <Gavel className="w-4 h-4 text-purple-400" />
          <span className="text-sm font-semibold text-white">Appeals</span>
        </div>
        <p className="text-xs text-glass-400">{error || 'No appeals for this ban.'}</p>
      </div>
    );
  }

  return (
    <div className={cn('', className)}>
      <div className="flex items-center gap-2 mb-2">
        <Gavel className="w-4 h-4 text-purple-400" />
        <span className="text-sm font-semibold text-white">Appeals</span>
        <span className="badge badge-neutral">{appeals.length}</span>
      </div>
      <div className="space-y-1.5">
        {appeals.map(appeal => {
          const v2Outcome = getV2Outcome(appeal.v2_stage1_result, appeal.v2_stage2_result);
          return (
          <Link
            key={appeal.id}
            to={`/appeals/${appeal.id}`}
            className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-md border border-glass-200/10 hover:bg-glass-200/10 hover:border-glass-200/20 group text-xs"
          >
            <div className="flex items-center gap-2">
              <span className="font-mono tabular-nums text-glass-400">#{appeal.id}</span>
              <AppealStatusBadge status={appeal.status} />
              <AppealVersionBadge appeal={appeal} />
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {appeal.verdict && (
                <span className={cn(
                  'font-semibold',
                  appeal.verdict === 'overturned' ? 'text-green-400' : 'text-red-400'
                )}>
                  {appeal.verdict}
                </span>
              )}
              {v2Outcome && (
                <span className={cn('font-semibold', V2_OUTCOME_META[v2Outcome].text)}>
                  {V2_OUTCOME_META[v2Outcome].label}
                </span>
              )}
              <span className="text-glass-400 tabular-nums">
                {formatDistanceToNow(new Date(appeal.created_at), { addSuffix: true })}
              </span>
              <ArrowRight className="w-3 h-3 text-glass-400 opacity-0 group-hover:opacity-100" />
            </div>
          </Link>
          );
        })}
      </div>
    </div>
  );
});
